'use client';

import { useCallback, useMemo, type ChangeEvent, type ReactNode } from 'react';
import { VStack, Box } from '@terra/ui-styled-system';
import { Textarea } from '../../components/textarea';
import { Select } from '../../components/select';
import { MetaDefault } from '../../components/typography';
import { EvidenceCardWithHar } from '../traces/EvidenceCardWithHar';
import { HttpEditor } from '../http-editor';
import { useTranslations } from 'next-intl';
import {
  PocEvidencePhaseSchema,
  PocEvidenceTypeSchema,
  type PocBlock,
  type PocEvidencePhase,
} from '@terra/shared-schemas-orchestrator';

const NO_PHASE_VALUE = '';
const DEFAULT_EVIDENCE_TYPE = PocEvidenceTypeSchema.enum.http;
const DEFAULT_ARTIFACT_TYPE = 'UNKNOWN' as const;
const PHASE_LABEL_KEYS = {
  BASELINE: 'phaseBaseline',
  DETECTION: 'phaseDetection',
  EXPLOITATION: 'phaseExploitation',
  VALIDATION: 'phaseValidation',
} as const satisfies Record<PocEvidencePhase, string>;
const ARTIFACT_TYPE_OPTIONS = [
  'UNKNOWN',
  'HTTP',
  'PYTHON',
  'CLI',
  'BROWSER',
  'TEXT',
] as const satisfies ReadonlyArray<
  NonNullable<Extract<PocBlock, { type: 'evidence' }>['artifactType']>
>;
const ARTIFACT_TYPE_LABEL_KEYS = {
  UNKNOWN: 'artifactTypeUnknown',
  HTTP: 'artifactTypeHttp',
  PYTHON: 'artifactTypePython',
  CLI: 'artifactTypeCli',
  BROWSER: 'artifactTypeBrowser',
  TEXT: 'artifactTypeText',
} as const satisfies Record<(typeof ARTIFACT_TYPE_OPTIONS)[number], string>;

type EvidenceBlockEditorProps = {
  block: Extract<PocBlock, { type: 'evidence' }>;
  index: number;
  onUpdate: (updated: PocBlock) => void;
};

type LabeledFieldProps = {
  label: string;
  children: ReactNode;
};

function LabeledField({ label, children }: LabeledFieldProps) {
  return (
    <Box>
      <MetaDefault color="fg.muted" mb={1}>
        {label}
      </MetaDefault>
      {children}
    </Box>
  );
}

type TextAreaFieldProps = {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  rows: number;
  mono?: boolean;
};

function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  rows,
  mono,
}: TextAreaFieldProps) {
  return (
    <LabeledField label={label}>
      <Textarea
        size="sm"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        resize="vertical"
        fontFamily={mono ? 'mono' : undefined}
      />
    </LabeledField>
  );
}

type HttpEditorFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
};

function HttpEditorField({
  label,
  value,
  onChange,
  placeholder,
}: HttpEditorFieldProps) {
  return (
    <LabeledField label={label}>
      <Box
        h="200px"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        overflow="hidden"
      >
        <HttpEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          showViewModeToggle
          showStatusBar={false}
        />
      </Box>
    </LabeledField>
  );
}

export function EvidenceBlockEditor({
  block,
  index,
  onUpdate,
}: EvidenceBlockEditorProps) {
  const t = useTranslations('designSystem.findings.structuredPocEditor');
  const tArtifacts = useTranslations('designSystem.artifacts');
  const evidenceType = block.evidenceType ?? DEFAULT_EVIDENCE_TYPE;
  const phaseOptions = useMemo(
    () => [
      { label: t('noStepLabel'), value: NO_PHASE_VALUE },
      ...PocEvidencePhaseSchema.options.map((phase) => ({
        label: t(PHASE_LABEL_KEYS[phase]),
        value: phase,
      })),
    ],
    [t],
  );
  const evidenceTypeOptions = useMemo(
    () =>
      PocEvidenceTypeSchema.options.map((type) => ({
        label: tArtifacts(type),
        value: type,
      })),
    [tArtifacts],
  );
  const artifactTypeOptions = useMemo(
    () =>
      ARTIFACT_TYPE_OPTIONS.map((type) => ({
        label: t(ARTIFACT_TYPE_LABEL_KEYS[type]),
        value: type,
      })),
    [t],
  );
  const handlePhaseChange = useCallback(
    (nextPhase: string) => {
      onUpdate({
        ...block,
        phase:
          nextPhase && nextPhase !== NO_PHASE_VALUE
            ? (nextPhase as PocEvidencePhase)
            : undefined,
      });
    },
    [block, onUpdate],
  );
  const handleEvidenceTypeChange = useCallback(
    (nextType: string) => {
      const evidenceTypeParsed = PocEvidenceTypeSchema.safeParse(nextType);

      if (!evidenceTypeParsed.success) {
        return;
      }

      const nextEvidenceType = evidenceTypeParsed.data;

      onUpdate({
        ...block,
        evidenceType: nextEvidenceType,
        artifactType:
          nextEvidenceType === 'artifact'
            ? (block.artifactType ?? DEFAULT_ARTIFACT_TYPE)
            : undefined,
      });
    },
    [block, onUpdate],
  );
  const handleArtifactTypeChange = useCallback(
    (nextType: string) => {
      const matchedType = ARTIFACT_TYPE_OPTIONS.find(
        (type) => type === nextType,
      );

      if (!matchedType) {
        return;
      }

      onUpdate({
        ...block,
        evidenceType: 'artifact',
        artifactType: matchedType,
      });
    },
    [block, onUpdate],
  );
  const handleExplanationChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate({ ...block, explanation: e.target.value });
    },
    [block, onUpdate],
  );
  const handleWhyItMattersChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate({ ...block, whyItMatters: e.target.value });
    },
    [block, onUpdate],
  );
  const handleManualRequestChange = useCallback(
    (value: string) => {
      onUpdate({ ...block, manualRequest: value });
    },
    [block, onUpdate],
  );
  const handleManualResponseChange = useCallback(
    (value: string) => {
      onUpdate({ ...block, manualResponse: value });
    },
    [block, onUpdate],
  );
  const hasHar = !!block.toolExecutionHarId;
  const isArtifact = evidenceType === 'artifact';
  const isPayloadEvidence =
    evidenceType === 'email' ||
    evidenceType === 'websocket' ||
    evidenceType === 'oob';

  return (
    <Box
      pr={16}
      w="full"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      overflow="hidden"
      bg="bg.canvas"
    >
      <VStack gap={2} p={3} alignItems="stretch">
        {!hasHar && (
          <MetaDefault color="fg.muted">
            {t('evidence')} {index + 1}
          </MetaDefault>
        )}
        {!hasHar && (
          <Select.SelectWithOptions
            label={t('evidenceFormat')}
            options={evidenceTypeOptions}
            value={[evidenceType]}
            onValueChange={handleEvidenceTypeChange}
            placeholder={t('evidenceFormat')}
            borderRadius="md"
          />
        )}
        {!hasHar && isArtifact && (
          <Select.SelectWithOptions
            label={t('artifactFormat')}
            options={artifactTypeOptions}
            value={[block.artifactType ?? DEFAULT_ARTIFACT_TYPE]}
            onValueChange={handleArtifactTypeChange}
            placeholder={t('artifactFormat')}
            borderRadius="md"
          />
        )}
        <Select.SelectWithOptions
          label={t('stepLabel')}
          options={phaseOptions}
          value={[block.phase ?? NO_PHASE_VALUE]}
          onValueChange={handlePhaseChange}
          placeholder={t('noStepLabel')}
          borderRadius="md"
        />
        <TextAreaField
          label={t('explanation')}
          value={block.explanation}
          onChange={handleExplanationChange}
          placeholder={t('explanationPlaceholder')}
          rows={2}
        />
        <TextAreaField
          label={t('whyItMatters')}
          value={block.whyItMatters ?? ''}
          onChange={handleWhyItMattersChange}
          placeholder={t('whyItMattersPlaceholder')}
          rows={1}
        />
        {!hasHar && isArtifact && (
          <>
            <TextAreaField
              label={t('action')}
              value={block.manualRequest ?? ''}
              onChange={(e) => handleManualRequestChange(e.target.value)}
              placeholder={t('actionPlaceholder')}
              rows={4}
              mono
            />
            <TextAreaField
              label={t('result')}
              value={block.manualResponse ?? ''}
              onChange={(e) => handleManualResponseChange(e.target.value)}
              placeholder={t('resultPlaceholder')}
              rows={4}
              mono
            />
          </>
        )}
        {!hasHar && isPayloadEvidence && (
          <TextAreaField
            label={t('payload')}
            value={block.manualResponse ?? ''}
            onChange={(e) => handleManualResponseChange(e.target.value)}
            placeholder={t('payloadPlaceholder')}
            rows={8}
            mono
          />
        )}
        {!hasHar && !isArtifact && !isPayloadEvidence && (
          <>
            <HttpEditorField
              label={t('manualRequest')}
              value={block.manualRequest ?? ''}
              onChange={handleManualRequestChange}
              placeholder={t('manualRequestPlaceholder')}
            />
            <HttpEditorField
              label={t('manualResponse')}
              value={block.manualResponse ?? ''}
              onChange={handleManualResponseChange}
              placeholder={t('manualResponsePlaceholder')}
            />
          </>
        )}
        {hasHar && (
          <EvidenceCardWithHar
            toolExecutionHarId={block.toolExecutionHarId}
            evidenceType={block.evidenceType}
            index={index}
            defaultExpanded={false}
          />
        )}
      </VStack>
    </Box>
  );
}
